import * as React from "react";
import { FindInStoreDrawer } from "./FindInStoreDrawer";
import ShopifyProduct from "@app/interface/ShopifyProduct";
import { useStorage } from "@app/hooks/useStorage";

import { convertDistance, getDistance } from "geolib";

type Props = {
  product: ShopifyProduct;
  moneyFormat: string;
};
const FindInStore: React.FC<Props> = ({ product, moneyFormat }) => {
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [savedLocation, setSavedLocation] = React.useState(false);
  const [selectedStore, setSelectedStore] = React.useState("");
  const [storesWithinRange, setStoresWithinRange] = React.useState<any[]>([]);
  const [selectedVariant, setSelectedVariant] = React.useState(product.variants[0].sku.toString());
  const [search, setSearch] = React.useState({ value: "", error: "" });

  // Listen for swatchSelected custom event to update selectedVariant
  React.useEffect(() => {
    const handleSwatchSelected = (event: CustomEvent) => {
      if (event.detail && event.detail.sku) {
        setSelectedVariant(event.detail.sku);
      }
    };
    window.addEventListener("swatchSelected", handleSwatchSelected as EventListener);
    return () => {
      window.removeEventListener("swatchSelected", handleSwatchSelected as EventListener);
    };
  }, []);

  const { getStorage, setStorage } = useStorage();

  const range = 100;
  const skus = product.variants.map(({ sku }) => sku).filter(Boolean);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!skus?.length) {
      setError("Cannot find any SKUs.");
      throw new Error("No SKUs for the selected product");
    }
    setError("");
    getStoresWithinRange(skus);
    if (savedLocation) {
      setStorage("findInStoreLocation", search.value);
    }
  };

  const getStoresWithinRange = async (skus: string[]) => {
    try {
      setIsLoading(true);

      const body = {
        skus: skus.join(","),
        type: "search",
        search: search.value,
      };

      const response = await fetch(
        "https://australia-southeast1-effortless-fort-408106.cloudfunctions.net/findInStore",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
      );

      if (!response.ok) {
        setError("Failed to fetch stores.");
        return [];
      }

      const responseData = await response.json();

      const { geometry, search: searchValue, stores } = responseData || {};

      if (!(stores?.length > 0) || !geometry?.latitude || !geometry?.longitude) {
        return [];
      }

      const storesWithFISEnabled = stores.filter((store) => {
        const fields = store.fields || [];
        const fisEnabledField = fields.find((field) => field.key === "find_in_store");
        return fisEnabledField && fisEnabledField.value === "true";
      });

      const storesWithDistance = storesWithFISEnabled
        .map((store) => {
          const location = {
            latitude: parseFloat(store.fields.find((field) => field.key === "address_latitude").value) || null,
            longitude: parseFloat(store.fields.find((field) => field.key === "address_longitude").value) || null,
          };

          const distance =
            location.latitude && location.longitude && geometry.latitude && geometry.longitude
              ? getDistance(
                  { latitude: geometry.latitude, longitude: geometry.longitude },
                  { latitude: location.latitude, longitude: location.longitude },
                )
              : null;

          return {
            ...store,
            distance: distance ? convertDistance(distance, "km").toFixed(2) : null,
          };
        })
        .sort((a, b) => (range === 0 ? a.title - b.title : a.distance - b.distance));

      setStoresWithinRange(storesWithDistance);
      setError("");
      setSearch((prev) => ({
        ...prev,
        value: searchValue,
      }));

      if (savedLocation) {
        setStorage("findInStoreLocation", searchValue);
      }

      return storesWithDistance.filter(({ distance }) => !range || (distance !== null && distance <= range));
    } catch (error) {
      console.error("Error fetching stores:", error);
      setError("An error occurred while fetching stores.");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectedStore = (id: string) => {
    if (id === selectedStore) {
      setSelectedStore("");
      return;
    }
    setSelectedStore(id);
  };

  return (
    <>
      <button className="find-in-store__btn" onClick={() => setOpen(true)}>
        Find in Store
      </button>

      <FindInStoreDrawer
        open={open}
        onClose={handleClose}
        product={product}
        moneyFormat={moneyFormat}
        search={search}
        setSearch={setSearch}
        handleSubmit={handleSubmit}
        savedLocation={savedLocation}
        setSavedLocation={setSavedLocation}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        isLoading={isLoading}
        error={error}
        storesWithinRange={storesWithinRange}
        handleSelectedStore={handleSelectedStore}
        selectedStore={selectedStore}
      />
    </>
  );
};

export { FindInStore };
